// channelAddress — pure channel-addressing rules shared by the Channels step
// and the wizard's resume/hydration path.
//
// Lives in utils/ rather than beside StepChannels.tsx so that
// `wizardCompletion.ts` can use the same predicate the step itself uses
// without importing a component module (and everything it drags in) — the
// step's own "is this step complete?" answer and the answer computed when an
// existing org is resumed must never disagree.
//
// StepChannels re-exports the public helpers, so existing imports from
// "../steps/StepChannels" keep working.

import { ChannelType } from "../../../flatbuffers/generated/archive/channel-type";
import { ChannelInterface } from "../../../interfaces/Channel.interface";
import { ChannelConnectionType } from "../../../types/enums";
import { coerceFlatBufferText } from "../../../utils/bankFloorOpenings";

/**
 * The generated FlatBuffer `ChannelType` each wizard transport saves as.
 * Exhaustive over `ChannelConnectionType`, so adding a transport without its
 * FlatBuffer value is a compile error rather than a silent fallback to TCP.
 */
export const CHANNEL_CONNECTION_TYPE_TO_FBS: Record<ChannelConnectionType, ChannelType> = {
  [ChannelConnectionType.Tcp]: ChannelType.TCP,
  [ChannelConnectionType.Udp]: ChannelType.UDP,
  [ChannelConnectionType.Serial]: ChannelType.SERIAL,
  [ChannelConnectionType.Adapter]: ChannelType.ADAPTER,
};

/** Map FlatBuffer ChannelType numeric value to app-level ChannelConnectionType string. */
export const CHANNEL_TYPE_NAMES: Record<number, ChannelConnectionType> = Object.fromEntries(
  Object.entries(CHANNEL_CONNECTION_TYPE_TO_FBS).map(([name, fbs]) => [fbs, name]),
) as Record<number, ChannelConnectionType>;

function isValidPort(port: string): boolean {
  const n = parseInt(port, 10);
  return !Number.isNaN(n) && n >= 1 && n <= 65535;
}

export function validateSocketAddress(addr: string): boolean {
  if (!addr.trim()) return false;
  // IPv6 [::1]:8080
  const ipv6Match = addr.match(/^\[([0-9a-fA-F:]+)\]:(\d+)$/);
  if (ipv6Match) return isValidPort(ipv6Match[2]);
  // IPv4 or hostname: host:port (use lastIndexOf to handle IPv6 without brackets)
  const colonIdx = addr.lastIndexOf(":");
  if (colonIdx === -1) return false;
  const host = addr.substring(0, colonIdx);
  const port = addr.substring(colonIdx + 1);
  if (!isValidPort(port)) return false;
  // IPv4
  const ipv4Match = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (ipv4Match) {
    return [ipv4Match[1], ipv4Match[2], ipv4Match[3], ipv4Match[4]].every(
      (oct) => parseInt(oct, 10) <= 255,
    );
  }
  // hostname (at least one char, no leading/trailing dot/hyphen)
  return /^[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?$/.test(host);
}

/**
 * True when the value looks like a valid OPC UA secure-channel endpoint
 * URL: "opc.tcp://host:port". Deliberately separate from
 * `validateSocketAddress` — that helper requires a bare "host:port" with no
 * scheme, which an OPC UA endpoint is not.
 */
export function isValidOpcUaEndpointUrl(url: string): boolean {
  const match = url.trim().match(/^opc\.tcp:\/\/(.+)$/i);
  if (!match) return false;
  return validateSocketAddress(match[1]);
}

/** A channel record read for its OPC UA endpoint, in either spelling. */
export type OpcUaEndpointCarrier = Partial<Pick<ChannelInterface, "opcUaEndpointUrl">> & {
  opc_ua_endpoint_url?: string | Uint8Array | null;
};

/**
 * Read a channel record's OPC UA endpoint, tolerantly.
 *
 * The FlatBuffer path surfaces it as `opcUaEndpointUrl`, a legacy REST/JSON
 * record as `opc_ua_endpoint_url`, and either may arrive as raw UTF-8 bytes
 * rather than a JS string — hence `coerceFlatBufferText`. An empty value and
 * an absent one both mean "this channel addresses by socket, not by endpoint",
 * so the read falls through from one spelling to the other the same way
 * `handleEditChannel`'s `d.opcUaEndpointUrl || d.opc_ua_endpoint_url` does.
 */
export function resolveChannelOpcUaEndpointUrl(ch: OpcUaEndpointCarrier): string {
  const camel = coerceFlatBufferText(ch.opcUaEndpointUrl);
  if (camel) return camel;
  return coerceFlatBufferText(ch.opc_ua_endpoint_url) ?? "";
}

/**
 * Picks the best socket address from an API channel response.
 * Which column holds it follows the channel type, not the protocol or the
 * transport: SINGLE channels store their address in vendor_socket_address,
 * ROUTABLE ones (CGA / LobbyVision / LobbyVision2 dispatching many banks over
 * one socket) in routable_socket_address. Reading is deliberately tolerant of
 * both so a channel still resolves while LiftCloud's socket-address migration
 * is moving a legacy row from one column to the other.
 * Empty strings are treated as absent — neither is a usable address.
 */
export function resolveChannelSocketAddress(
  ch: Pick<ChannelInterface, "routableSocketAddress" | "vendorSocketAddress">,
): string {
  return String(ch.vendorSocketAddress || ch.routableSocketAddress || "");
}

/** The three facts that decide whether a channel is usably addressed. */
export interface ChannelAddressFacts {
  type: ChannelConnectionType;
  /** vendor address if set, else routable address; `""` when neither is. */
  socketAddress: string;
  /** `opc.tcp://host:port` endpoint; `""` when the channel has none. */
  opcUaEndpointUrl: string;
}

/**
 * Check whether a channel has a valid address for its transport type.
 *
 * A TCP channel with no socket address still counts as addressed when it
 * carries a valid `opc.tcp://host:port` endpoint, because that endpoint IS its
 * vendor address — LiftCloud's `resolveOpcUaSocket` never reads the socket
 * column for OPC UA. This is the row-level form of the same excuse
 * `isChannelAddressingValid` grants the edit form; without it a legitimately
 * addressed OPC-UA-only channel failed `allChannelsValid`, which unmarked the
 * Channels step and stopped the wizard advancing even though every channel was
 * complete. UDP gets no such excuse — see `isChannelAddressingValid`.
 */
export function channelHasValidAddress(facts: ChannelAddressFacts): boolean {
  if (facts.type === ChannelConnectionType.Serial) return true; // device path, not IP:Port
  // ADAPTER has no socket by definition: LiftCloud reaches an OPC UA adapter
  // through the endpoint URL and a Numato controller board through the
  // adapter's own mapping. The bank step refuses an OPC UA bank whose channel
  // has no endpoint (opcUaChannelEndpointMissing), where the adapter is known.
  if (facts.type === ChannelConnectionType.Adapter) return true;
  if (validateSocketAddress(facts.socketAddress)) return true;
  return (
    facts.type === ChannelConnectionType.Tcp && isValidOpcUaEndpointUrl(facts.opcUaEndpointUrl)
  );
}

/**
 * A channel exactly as the wizard hydrator receives it from the channels list.
 *
 * The RTK query unpacks the FlatBuffer response, so production records are
 * camelCase `ChannelT` objects with a numeric `connectionType` and string
 * fields that may be raw UTF-8 bytes. The snake_case spellings are the legacy
 * REST/JSON shape and stay accepted so a JSON fallback response is judged the
 * same way.
 */
export type ChannelRecord = Partial<
  Pick<
    ChannelInterface,
    "connectionType" | "vendorSocketAddress" | "routableSocketAddress" | "opcUaEndpointUrl"
  >
> & {
  connection_type?: string | number | null;
  vendor_socket_address?: string | Uint8Array | null;
  routable_socket_address?: string | Uint8Array | null;
  opc_ua_endpoint_url?: string | Uint8Array | null;
};

/** Transport of a channel record; an unset or unrecognised type reads as TCP, as the step does. */
function channelRecordType(ch: ChannelRecord): ChannelConnectionType {
  if (typeof ch.connectionType === "number") {
    return CHANNEL_TYPE_NAMES[ch.connectionType] ?? ChannelConnectionType.Tcp;
  }
  const legacy = ch.connection_type;
  if (typeof legacy === "number") {
    return CHANNEL_TYPE_NAMES[legacy] ?? ChannelConnectionType.Tcp;
  }
  if (typeof legacy === "string") {
    const named = Object.values(ChannelConnectionType).find((t) => t === legacy.toUpperCase());
    if (named) return named;
  }
  return ChannelConnectionType.Tcp;
}

/**
 * Reduce a channel record from the API, in either spelling, to the facts
 * `channelHasValidAddress` judges. Vendor is read before routable and empty
 * counts as absent, matching `resolveChannelSocketAddress`.
 */
export function channelAddressFacts(ch: ChannelRecord): ChannelAddressFacts {
  return {
    type: channelRecordType(ch),
    socketAddress:
      coerceFlatBufferText(ch.vendorSocketAddress) ??
      coerceFlatBufferText(ch.vendor_socket_address) ??
      coerceFlatBufferText(ch.routableSocketAddress) ??
      coerceFlatBufferText(ch.routable_socket_address) ??
      "",
    opcUaEndpointUrl: resolveChannelOpcUaEndpointUrl(ch),
  };
}
